import { fmtNum } from '../ui/ListFoot';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Link, goBack, navigate, useRoute } from '../lib/router';
import { useNotice } from '../shell/notice';
import { trailSection } from '../shell/sections';
import { useTuck } from '../ui/useTuck';
import { TabRow } from '../ui/TabRow';
import { BackLink } from '../ui/BackLink';
import { BookCell, ListRow, ListTable, type Col, type TableSort } from '../ui/ListTable';
import { SectionFold, SectionFoldAll } from '../ui/SectionFold';
import { PillMenu, SettingMenu } from '../ui/PillMenu';
import { ToTop } from '../ui/ToTop';
import { ChipRow, FilterChip } from '../ui/bits';
import { useRemembered } from '../lib/remember';
import { BOOKS_CHANGED } from '../lib/changed';
import { createPortal } from 'preact/compat';
import { AddBook } from '../add/AddBook';
import { LITERARY_CHOICES, type Literary } from '../book/genres';
import { BulkMenu } from '../queue/parts';
import { BULK_WHERE, addSeveral, literaryGuess, passOn, whyFor, type BulkWhere } from './listAdd';
import { bookLabel } from '../book/model';
import {
  KIND_NAMES,
  KIND_ORDER,
  LITERARY_NAMES,
  booksText,
  columnsFor,
  kindCounts,
  kindLine,
  levelName,
  levelsLine,
  mineText,
  sortCatalog,
  sortEntries,
  youLine,
  type CatalogList,
  type CatalogSort,
  type CatalogSortKey,
  type EntrySort,
  type EntrySortKey,
  type ListKind,
} from './lists';
import { followList, hideList, loadCatalog, loadListPage, type Catalog, type ListPageData } from './listsLoad';
import './lists.css';

// Discover (Phase 7 Part 3a; boards 11, 14, 12, 12p; Kristi Nov 3): the Awards & lists tab
// (one tab until Authors & narrators and New for you are built) and each list's page,
// /discover/lists/<id>/<year>. Browsing only: adding books from a list is Part 3b.

const DISCOVER = '/discover';

/** /discover/lists/<id>[/<year>] → the list and year asked for. */
export function listRoute(path: string): { id: string; year: number | null } | null {
  const m = path.match(/^\/discover\/lists\/([0-9a-fA-F-]{36})(?:\/(\d{4}))?\/?$/);
  return m ? { id: m[1]!.toLowerCase(), year: m[2] ? Number(m[2]) : null } : null;
}

export function DiscoverPage({ wide }: { wide: boolean }) {
  const { path, state } = useRoute();
  // Under a book's side panel the address is the book's; the page keeps the one it was opened from.
  const here = path.startsWith(DISCOVER) ? path : (state.from ?? DISCOVER);
  const list = listRoute(here);
  const tucked = useTuck(wide);
  if (list) return <ListPage id={list.id} year={list.year} wide={wide} here={here} />;
  return (
    <div class={`page queue discover${wide ? '' : ' narrow-list'}${tucked ? ' tucked' : ''}`}>
      {wide ? <h1 class="visually-hidden">Discover</h1> : null}
      <div class="shelves-top queue-top">
        <TabRow label="Discover" value="lists" tabs={[{ key: 'lists', name: 'Awards & lists', short: 'Lists' }]} onPick={() => navigate(DISCOVER)} />
        <span class="mb-bar-end">
          <SectionFoldAll />
        </span>
        <ToTop wide={wide} />
      </div>
      <AwardsAndLists wide={wide} />
      <ToTop wide={wide} float />
    </div>
  );
}

function useCatalog() {
  const [cat, setCat] = useState<Catalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    loadCatalog()
      .then((c) => {
        setCat(c);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  useEffect(() => {
    void load();
    const again = () => void load();
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, []);
  return { cat, error, reload: load };
}

function AwardsAndLists({ wide }: { wide: boolean }) {
  const notice = useNotice();
  const { cat, error, reload } = useCatalog();
  const [kinds, setKinds] = useRemembered<ListKind[]>('discover:kinds', []);
  const [sort, setSort] = useRemembered<CatalogSort>('discover:sort', { key: 'kind' });
  if (error) return <div class="heads-up">Couldn’t load the lists: {error}</div>;
  if (!cat) return <p class="shelves-quiet">Loading the lists…</p>;
  const shown = cat.lists.filter((l) => !l.hidden);
  const hidden = cat.lists.filter((l) => l.hidden);
  const followed = shown.filter((l) => l.following);
  const counts = kindCounts(shown);
  const rows = sortCatalog(
    shown.filter((l) => !kinds.length || kinds.includes(l.kind)),
    sort,
  );
  const flip = (k: CatalogSortKey) => setSort(sort.key === k ? { key: k, desc: !sort.desc } : { key: k, desc: k === 'mine' });
  const done = () => void reload();
  const follow = (l: CatalogList) => (
    <button type="button" class={`btn-secondary${l.following ? ' on' : ''}`} aria-pressed={l.following} onClick={() => void followList(notice, l, !l.following, done)}>
      {l.following ? 'Following ✓' : '+ Follow'}
    </button>
  );
  const hide = (l: CatalogList) => (
    <button type="button" class="btn-link" onClick={() => void hideList(notice, l, true, done)}>
      Hide
    </button>
  );
  const open = (l: CatalogList) => `/discover/lists/${l.id}`;
  const cols: Col<CatalogSortKey>[] = [
    { label: 'List', width: 'minmax(0, 2fr)', sort: 'name' },
    { label: 'Kind', width: 'minmax(0, 1fr)', sort: 'kind' },
    { label: 'How often', width: 'minmax(0, 1fr)', sort: 'frequency' },
    { label: 'Since', width: '72px', sort: 'since', num: true },
    { label: 'On your list', width: '112px', sort: 'mine', num: true },
    { width: '176px' },
  ];
  return (
    <>
      <p class="queue-description">Follow awards and best-of lists, and see which of their books are on your list.</p>
      <p class="mb-needs quiet">
        {shown.length} {shown.length === 1 ? 'list' : 'lists'} in the catalog · {followed.length} followed
      </p>
      {followed.length ? (
        <SectionFold store="discover" id="following" title="Lists you follow" count={followed.length} noun={['list', 'lists']}>
          <div class="dl-cards">
            {followed.map((l) => {
              const y = cat.latest.get(l.id);
              const pct = y && y.total ? Math.round((y.mine / y.total) * 100) : 0;
              return (
                <article key={l.id} class="dl-card">
                  <p class="bk-label">{kindLine(l)}</p>
                  <h3 class="dl-card-name">
                    {l.name} {y ? <span class="dl-card-year">{y.year}</span> : null}
                  </h3>
                  {y ? (
                    <>
                      <span class="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${l.name} ${y.year}: on your list`}>
                        <span style={{ width: `${pct}%` }} />
                      </span>
                      <p class="dl-card-line">
                        {fmtNum(y.mine)} on your list · {fmtNum(y.total - y.mine)} not on it
                      </p>
                    </>
                  ) : null}
                  <div class="dl-card-actions">
                    <Link href={open(l)} class="btn-secondary">
                      See the list ›
                    </Link>
                    {follow(l)}
                  </div>
                </article>
              );
            })}
          </div>
        </SectionFold>
      ) : null}
      <SectionFold store="discover" id="catalog" title="Browse the catalog" count={shown.length} noun={['list', 'lists']}>
        <p class="lt-order">Shared by everyone using the app. Hide a list you’re not interested in: it leaves the catalog and is unfollowed.</p>
        <ChipRow label="Kinds of list">
          {KIND_ORDER.map((k) => (
            <FilterChip
              key={k}
              label={KIND_NAMES[k][1]}
              count={counts[k] ?? 0}
              on={kinds.includes(k)}
              onToggle={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])}
            />
          ))}
        </ChipRow>
        {!wide ? (
          <div class="pp-sortline">
            <SettingMenu<CatalogSortKey>
              name="Sort"
              choices={[
                ['kind', 'Kind'],
                ['name', 'List'],
                ['since', 'Since'],
                ['mine', 'On your list'],
              ]}
              value={sort.key}
              onChange={(k) => setSort({ key: k, desc: k === 'mine' })}
            />
          </div>
        ) : null}
        <ListTable<CatalogSortKey> label="The lists catalog" cols={cols} sort={sort as TableSort<CatalogSortKey>} onSort={flip} empty="No lists of this kind.">
          {rows.map((l) => {
            const name = (
              <span class="dl-name">
                <Link href={open(l)} class="lt-title">
                  {l.name}
                </Link>
                {levelsLine(l) ? <span class="lt-author">{levelsLine(l)}</span> : null}
              </span>
            );
            const actions = (
              <span class="dl-actions">
                {follow(l)}
                {hide(l)}
              </span>
            );
            return (
              <ListRow
                key={l.id}
                data-row={l.id}
                cells={[name, KIND_NAMES[l.kind][0], l.frequency ?? '', l.since ?? '', booksText(l.onYourList), actions]}
                phone={{ book: name, control: actions, line: [KIND_NAMES[l.kind][0], l.frequency, l.since ? `since ${l.since}` : null, l.onYourList ? `${booksText(l.onYourList)} on your list` : null].filter(Boolean).join(' · ') }}
              />
            );
          })}
        </ListTable>
      </SectionFold>
      {hidden.length ? (
        <SectionFold store="discover" id="hidden" title="Hidden lists" count={hidden.length} noun={['list', 'lists']} closed>
          <ul class="dl-hidden">
            {hidden.map((l) => (
              <li key={l.id}>
                <span>{l.name}</span>
                <button type="button" class="btn-link" onClick={() => void hideList(notice, l, false, done)}>
                  Show again
                </button>
              </li>
            ))}
          </ul>
        </SectionFold>
      ) : null}
    </>
  );
}

// ---------- a list's page ----------

function ListPage({ id, year, wide, here }: { id: string; year: number | null; wide: boolean; here: string }) {
  const notice = useNotice();
  const { state } = useRoute();
  const tucked = useTuck(wide);
  const [data, setData] = useState<ListPageData | null | 'missing'>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<'' | 'mine' | 'not'>('');
  const [sort, setSort] = useState<EntrySort>({ key: 'order' });
  const load = () =>
    loadListPage(id, year)
      .then((d) => {
        setData(d ?? 'missing');
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  useEffect(() => {
    setData(null);
    void load();
    const again = () => void load();
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, [id, year]);

  // Opened from a book page or another shared page, ← Back names it ("← Back to Martyr!"); from
  // Discover, or opened directly, it's Awards & lists (Kristi, Nov 3).
  const fromElsewhere = !!state.from && !state.from.startsWith(DISCOVER);
  const back = <BackLink where={fromElsewhere ? (state.fromName ?? trailSection(state).name) : 'Awards & lists'} onClick={() => goBack(DISCOVER)} />;
  const d = data && data !== 'missing' ? data : null;
  const levels = d?.list.levels ?? [];
  const shownRows = useMemo(() => {
    if (!d) return [];
    const list = d.entries.filter((e) => (show === 'mine' ? e.mine : show === 'not' ? !e.mine : true));
    return sortEntries(list, levels, sort);
  }, [d, show, sort]);

  const body = error ? (
    <div class="heads-up">Couldn’t load the list: {error}</div>
  ) : data === 'missing' ? (
    <div class="heads-up">There’s no such list in the catalog.</div>
  ) : !d ? (
    <p class="shelves-quiet">Loading the list…</p>
  ) : (
    <ListBody d={d} wide={wide} show={show} setShow={setShow} sort={sort} setSort={setSort} rows={shownRows} here={here} />
  );

  const head = d ? (
    <header class="pp-head dl-head">
      <p class="bk-label">{[kindLine(d.list), d.list.publisher].filter(Boolean).join(' · ')}</p>
      <div class="dl-title-row">
        {/* On a phone the section's top bar holds the page's h1 (Discover). */}
        {wide ? <h1 class="book-title pp-name">{d.list.name}</h1> : <h2 class="book-title pp-name">{d.list.name}</h2>}
        {d.list.years.length > 1 ? (
          <PillMenu
            label={
              <>
                Year: <span class="pill-menu-value">{d.year}</span>
              </>
            }
            ariaLabel={`Year: ${d.year}`}
            sheetTitle="Year"
            choices={d.list.years.map((y) => ({ value: y, label: String(y) }))}
            current={d.year}
            onPick={(y) => navigate(`/discover/lists/${d.list.id}/${y}`, { replace: true, state })}
          />
        ) : (
          <span class="pill">{d.year}</span>
        )}
        <span class="dl-follow">
          <button
            type="button"
            class={`btn-secondary${d.list.following ? ' on' : ''}`}
            aria-pressed={d.list.following}
            onClick={() => void followList(notice, d.list, !d.list.following, () => void load())}
          >
            {d.list.following ? 'Following ✓' : '+ Follow'}
          </button>
        </span>
      </div>
      {d.labels.some(Boolean) ? <p class="pp-counts">{d.labels.filter(Boolean).join(' · ')}</p> : null}
      <p class="pp-counts">
        {d.source ? (
          <>
            Source:{' '}
            <a href={d.source} target="_blank" rel="noopener noreferrer">
              {sourceName(d.source)} ↗
            </a>
          </>
        ) : null}
        {d.list.since ? `${d.source ? ' · ' : ''}since ${d.list.since}` : ''}
      </p>
      {/* The NYT's terms ask for its credit beside its data. */}
      {d.list.source?.startsWith('Data provided by') ? <p class="pp-counts">{d.list.source}</p> : null}
    </header>
  ) : null;

  // A page inside Discover (the section's own top bar on a phone), with ← Back at its top.
  return (
    <div class={`page pp-page dl-page${tucked ? ' tucked' : ''}`}>
      <div class="pp-back">{back}</div>
      {head}
      {body}
      <ToTop wide={wide} float />
    </div>
  );
}

const sourceName = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

function ListBody({
  d,
  wide,
  show,
  setShow,
  sort,
  setSort,
  rows,
  here,
}: {
  d: ListPageData;
  wide: boolean;
  show: '' | 'mine' | 'not';
  setShow: (s: '' | 'mine' | 'not') => void;
  sort: EntrySort;
  setSort: (s: EntrySort) => void;
  rows: ListPageData['entries'];
  here: string;
}) {
  const notice = useNotice();
  const [adding, setAdding] = useState<ListPageData['entries'][number] | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [asking, setAsking] = useState<string | null>(null);
  const [bulkLiterary, setBulkLiterary] = useState<Literary | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const guess = literaryGuess(d.entries);
  const on = d.entries.filter((e) => e.mine).length;
  const pct = d.entries.length ? Math.round((on / d.entries.length) * 100) : 0;
  const c = columnsFor(d.entries);
  const flip = (k: EntrySortKey) => setSort(sort.key === k ? { key: k, desc: !sort.desc } : { key: k });
  const open = (label: number) => () => navigate(`/book/${bookLabel(label)}`, { state: { from: here }, keepScroll: wide });
  const cols: Col<EntrySortKey>[] = [
    ...(c.rank ? [{ label: '#', width: '48px', sort: 'order' as const, num: true }] : []),
    { label: 'Book', width: 'minmax(0, 2fr)', sort: 'title', book: true },
    { label: 'Author', width: 'minmax(0, 1.2fr)', sort: 'author' },
    ...(c.level ? [{ label: 'Level', width: 'minmax(0, 0.8fr)', sort: 'level' as const }] : []),
    ...(c.category ? [{ label: 'Category', width: 'minmax(0, 1fr)', sort: 'category' as const }] : []),
    { label: 'Literary genre', width: 'minmax(0, 0.9fr)', sort: 'literary' },
    { label: 'Your list', width: 'minmax(0, 0.9fr)', sort: 'mine' },
    { width: '200px' },
  ];
  if (selecting) cols.unshift({ width: '36px' });
  const addable = rows.filter((e) => !e.mine);
  const picked = d.entries.filter((e) => selected.includes(e.id) && !e.mine);
  const pickedUnknown = picked.filter((e) => !e.literary).length;
  // Not for me, ticked: passed on at once (Kristi, Nov 3); a book the catalog has no literary genre for asks first.
  const pass = async (e: ListPageData['entries'][number], lit: Literary | null) => {
    if (!e.literary && !lit) return setAsking(e.id);
    setAsking(null);
    setBusy(e.id);
    await passOn(notice, e, (e.literary as Literary | null) ?? lit!, whyFor(d.list.name, d.year, e));
    setBusy(null);
  };
  const bulk = async (where: BulkWhere) => {
    const lit = bulkLiterary ?? guess;
    if (pickedUnknown && !lit) return notice({ message: 'Choose a literary genre for the books the catalog doesn’t know.', done: false });
    if (picked.length > 100) return notice({ message: 'At most 100 books at a time.', done: false });
    setBusy('bulk');
    const ok = await addSeveral(notice, d.list.name, d.year, picked, where, lit);
    setBusy(null);
    if (ok) {
      setSelected([]);
      setSelecting(false);
    }
  };
  return (
    <>
      <section class="dl-you" aria-label="You and this list">
        <p class="bk-label">You and this list</p>
        <span class="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="On your list">
          <span style={{ width: `${pct}%` }} />
        </span>
        <p class="dl-card-line">
          {youLine(d.entries)} · {fmtNum(d.entries.length)} {d.entries.length === 1 ? 'book' : 'books'}
        </p>
      </section>
      <div class="dl-tools">
        <ChipRow label="Show">
          <FilterChip label="Not on your list" count={d.entries.length - on} on={show === 'not'} onToggle={() => setShow(show === 'not' ? '' : 'not')} />
          <FilterChip label="On your list" count={on} on={show === 'mine'} onToggle={() => setShow(show === 'mine' ? '' : 'mine')} />
        </ChipRow>
        <button type="button" class={`btn-secondary${selecting ? ' on' : ''}`} aria-pressed={selecting} onClick={() => (setSelecting(!selecting), setSelected([]))}>
          Select several
        </button>
        {!wide ? (
          <SettingMenu<EntrySortKey>
            name="Sort"
            choices={[
              ['order', 'The list’s order'],
              ['title', 'Title'],
              ['author', 'Author'],
              ...(c.level ? ([['level', 'Level']] as [EntrySortKey, string][]) : []),
              ['mine', 'Your list'],
            ]}
            value={sort.key}
            onChange={(k) => setSort({ key: k })}
          />
        ) : null}
      </div>
      {selecting ? (
        <div class="queue-bulk dl-bulk" role="region" aria-label="Add the selected books">
          <span class="queue-bulk-count">{picked.length ? `${fmtNum(picked.length)} selected` : 'None selected'}</span>
          <button type="button" class="btn-link" onClick={() => setSelected(addable.map((e) => e.id))}>
            Select all {fmtNum(addable.length)} not on your list
          </button>
          {picked.length ? (
            <>
              {pickedUnknown ? (
                <PillMenu<Literary>
                  label={
                    <>
                      Literary genre for {fmtNum(pickedUnknown)}: <span class="pill-menu-value">{LITERARY_NAMES[bulkLiterary ?? guess ?? ''] ?? 'Choose'}</span>
                    </>
                  }
                  ariaLabel="Literary genre for the books the catalog doesn’t know"
                  sheetTitle="Literary genre"
                  empty={!(bulkLiterary ?? guess)}
                  choices={LITERARY_CHOICES.map((l) => ({ value: l.key, label: l.name }))}
                  current={bulkLiterary ?? guess}
                  onPick={setBulkLiterary}
                />
              ) : null}
              <BulkMenu label={busy === 'bulk' ? 'Adding…' : `Add ${fmtNum(picked.length)} to`} items={BULK_WHERE.map(([w, label]) => ({ label, run: () => void bulk(w) }))} note="Added together as one import: Past imports and Recently added list it, with Undo import." />
            </>
          ) : null}
        </div>
      ) : null}
      {wide ? <p class="lt-order">{c.rank ? 'In the list’s own order (#) · sort by any header' : 'Winners first · sort by any header'}</p> : null}
      <ListTable<EntrySortKey> label={`${d.list.name} ${d.year}`} cols={cols} sort={sort as TableSort<EntrySortKey>} onSort={flip} class="dl-table" empty="No books here match.">
        {rows.map((e) => {
          // A book on your list opens its page; the others are names only until Part 3b adds them.
          const cell = <BookCell title={e.title} author={wide ? undefined : e.author} cover={e.cover} onOpen={e.mine ? open(e.mine.label) : undefined} />;
          const mine = e.mine ? <span class="pill">{mineText(e)}</span> : '';
          const check = selecting ? (
            <span class="q-check">{e.mine ? null : <input type="checkbox" aria-label={`Select ${e.title}`} checked={selected.includes(e.id)} onChange={() => setSelected(selected.includes(e.id) ? selected.filter((x) => x !== e.id) : [...selected, e.id])} />}</span>
          ) : null;
          const actions = e.mine ? null : asking === e.id ? (
            <span class="dl-ask" role="group" aria-label={`Literary genre for ${e.title}`}>
              {LITERARY_CHOICES.filter((l) => l.key !== 'drama').map((l) => (
                <button key={l.key} type="button" class="btn-secondary" onClick={() => void pass(e, l.key as Literary)}>
                  {l.name}
                </button>
              ))}
              <button type="button" class="btn-link" onClick={() => setAsking(null)}>
                Cancel
              </button>
            </span>
          ) : (
            <span class="dl-row-actions">
              <button type="button" class="btn-secondary" disabled={busy === e.id} onClick={() => setAdding(e)}>
                Add…
              </button>
              <label class="dl-nfm">
                <input type="checkbox" checked={false} disabled={busy === e.id} onChange={() => void pass(e, guess)} /> Not for me
              </label>
            </span>
          );
          return (
            <ListRow
              key={e.id}
              data-row={e.id}
              cells={[
                ...(selecting ? [check] : []),
                ...(c.rank ? [e.rank ?? ''] : []),
                cell,
                e.author,
                ...(c.level ? [levelName(e.level)] : []),
                ...(c.category ? [e.category ?? ''] : []),
                e.literary ? (LITERARY_NAMES[e.literary] ?? e.literary) : '',
                mine,
                actions,
              ]}
              phone={{
                lead: check ?? (c.rank && e.rank !== null ? <span class="pp-num">{e.rank}</span> : undefined),
                book: cell,
                control: e.mine ? <span class="pill">{mineText(e)}</span> : (actions ?? undefined),
                line: [c.level ? levelName(e.level) : null, e.category].filter(Boolean).join(' · ') || undefined,
              }}
            />
          );
        })}
      </ListTable>
      {adding
        ? createPortal(
            <AddBook
              onClose={() => setAdding(null)}
              fromList={{ entry: adding.id, title: adding.title, author: adding.author, why: whyFor(d.list.name, d.year, adding), literary: (adding.literary as Literary | null) ?? guess }}
            />,
            document.body,
          )
        : null}
    </>
  );
}
